import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  UserManager,
  WebStorageStateStore,
  type User,
  type UserManagerSettings
} from 'oidc-client-ts';
import { apiConfig } from '@/api/config';
import { setAuthToken } from './token';

/**
 * Scopes mirror the backend SecurityConfig:
 *   admin    — model design, metadata, registry export + audit
 *   internal — back-office writes (quotations, work orders, dispatch, etc.)
 *   mobile   — customer self-service (warranty register, claim submit)
 *
 * For the pilot, if the signed-in user has no matching Keycloak realm roles
 * we grant all three so demo accounts work without RBAC setup. Tighten this
 * behavior once real users + role mappings exist.
 */
export type Scope = 'admin' | 'internal' | 'mobile';

export interface AuthSession {
  token: string;
  /** Keycloak sub — stable unique id for the user. Use for API assignee lookups. */
  subject: string;
  /**
   * Human-readable name for the topbar / greeting. Sourced from Keycloak in
   * priority order: preferred_username → name → email → subject (UUID fallback).
   * If all upstream claims are missing (rare — misconfigured realm), falls
   * back to "User" so the UI still renders something sane.
   */
  displayName: string;
  scopes: Scope[];
  /** ms epoch — Date.now() > expiresAt means the token has expired. */
  expiresAt: number;
}

export interface AuthContextValue {
  session: AuthSession | null;
  /** True while we're checking storage / negotiating with Keycloak on first mount. */
  loading: boolean;
  hasScope: (scope: Scope) => boolean;
  /** Redirect to Keycloak. Optional `from` is where to bounce back to post-callback. */
  login: (from?: string) => Promise<void>;
  /** RP-initiated logout — clears local session then redirects to Keycloak's end-session endpoint. */
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

const userManagerSettings: UserManagerSettings = {
  authority: apiConfig.oauthIssuerUrl,
  client_id: apiConfig.oauthClientId,
  redirect_uri: `${window.location.origin}/callback`,
  post_logout_redirect_uri: `${window.location.origin}/`,
  response_type: 'code',
  scope: 'openid',
  automaticSilentRenew: true,
  // Keycloak public clients get roles + email directly in the ID token, so
  // loadUserInfo isn't needed and can 401 on some realm configs.
  loadUserInfo: false,
  userStore: new WebStorageStateStore({ store: window.localStorage })
};

/** Module-level singleton — one UserManager per SPA instance. */
export const userManager = new UserManager(userManagerSettings);

const ALL_SCOPES: Scope[] = ['admin', 'internal', 'mobile'];

function scopesFromUser(user: User): Scope[] {
  const claims = user.profile as Record<string, unknown>;
  const realmAccess = claims.realm_access as { roles?: string[] } | undefined;
  const roles = realmAccess?.roles ?? [];
  const mapped = roles.filter(
    (r): r is Scope => r === 'admin' || r === 'internal' || r === 'mobile'
  );
  return mapped.length > 0 ? mapped : ALL_SCOPES;
}

/**
 * Best-effort JWT payload decoder. Returns null on any malformed token —
 * consumers must treat this as a hint, never a security check (the token
 * has NOT been verified; the backend does that on every API call).
 */
function decodeJwtPayload(token: string | undefined): Record<string, unknown> {
  if (!token) return {};
  try {
    const [, payload] = token.split('.');
    if (!payload) return {};
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
      + '='.repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(base64)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function displayNameFromUser(user: User): string {
  // Read from BOTH the id_token (user.profile) and the access_token payload.
  // Keycloak often puts these claims in the access token even when the SPA
  // requests only `openid` scope (no `profile` scope), so the fallback keeps
  // the topbar readable without requiring realm-side scope config.
  //
  // Priority: full name > given+family > preferred_username > email > sub.
  // The user asked for the human name in the topbar (not the login handle).
  const idClaims = user.profile as Record<string, unknown>;
  const accessClaims = decodeJwtPayload(user.access_token);

  const composedName =
    typeof idClaims.given_name === 'string' && typeof idClaims.family_name === 'string'
      ? `${idClaims.given_name} ${idClaims.family_name}`
      : typeof accessClaims.given_name === 'string' && typeof accessClaims.family_name === 'string'
        ? `${accessClaims.given_name} ${accessClaims.family_name}`
        : undefined;

  const candidates = [
    idClaims.name,
    accessClaims.name,
    composedName,
    idClaims.preferred_username,
    accessClaims.preferred_username,
    idClaims.email,
    accessClaims.email
  ];

  for (const c of candidates) {
    if (typeof c === 'string' && c.length > 0) return c;
  }
  return user.profile.sub || 'User';
}

function sessionFromUser(user: User): AuthSession {
  return {
    token: user.access_token,
    subject: user.profile.sub ?? 'unknown',
    displayName: displayNameFromUser(user),
    scopes: scopesFromUser(user),
    expiresAt: (user.expires_at ?? 0) * 1000
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    userManager
      .getUser()
      .then((user) => {
        if (!mounted) return;
        if (user && !user.expired) {
          setAuthToken(user.access_token);
          setSession(sessionFromUser(user));
        }
      })
      .catch(() => {
        // Ignore — no user is a normal state on first load.
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    const onUserLoaded = (user: User) => {
      setAuthToken(user.access_token);
      setSession(sessionFromUser(user));
    };
    const onUserUnloaded = () => {
      setAuthToken(null);
      setSession(null);
    };
    const onAccessTokenExpired = () => {
      // automaticSilentRenew will try to refresh; if that fails, userUnloaded fires.
      setAuthToken(null);
    };

    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addUserUnloaded(onUserUnloaded);
    userManager.events.addAccessTokenExpired(onAccessTokenExpired);

    return () => {
      mounted = false;
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeUserUnloaded(onUserUnloaded);
      userManager.events.removeAccessTokenExpired(onAccessTokenExpired);
    };
  }, []);

  const login = useCallback(async (from?: string) => {
    await userManager.signinRedirect({ state: { from } });
  }, []);

  const logout = useCallback(async () => {
    await userManager.signoutRedirect();
  }, []);

  const hasScope = useCallback(
    (scope: Scope) => session?.scopes.includes(scope) ?? false,
    [session]
  );

  const value = useMemo<AuthContextValue>(
    () => ({ session, loading, hasScope, login, logout }),
    [session, loading, hasScope, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
